import { useId, useRef, useState, type ReactNode } from "react";
import * as Dialogo from "@radix-ui/react-dialog";
import { AlertCircle, AlertTriangle } from "lucide-react";
import { Botao } from "./Botao";
import { Dica } from "./Dica";
import { useDevolverFoco } from "./useDevolverFoco";
import { textoDoErro } from "@/api/problema";

/*
 * Os três níveis de confirmação do doc 06a §5.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A SEVERIDADE É ESCOLHIDA PELA CONSEQUÊNCIA, E NÃO PELO GOSTO.           │
 * │                                                                          │
 * │ leve       desbloquear um convênio. Reversível, efeito pequeno.          │
 * │ destrutiva cancelar boleto, revogar chave. Irreversível para um alvo.    │
 * │ critica    reprocessar 400 mensagens, resolver indeterminado. Efeito em  │
 * │            massa, ou decisão que substitui o julgamento do sistema.      │
 * │                                                                          │
 * │ A crítica exige DIGITAR uma confirmação e, quando a auditoria pede,      │
 * │ escrever uma justificativa. O atrito é o ponto: é o que separa "cliquei  │
 * │ sem ler" de "decidi".                                                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * O verbo do botão é o verbo do resultado (doc 06a §6): "Cancelar boleto",
 * nunca "Confirmar". Num diálogo cujo título já pergunta "Cancelar o boleto
 * PED-88231?", um botão "Confirmar" ao lado de um "Cancelar" é ambíguo de um
 * jeito que custa dinheiro.
 */
export type Severidade = "leve" | "destrutiva" | "critica";

export interface PedidoDeConfirmacao {
  severidade: Severidade;
  titulo: string;
  /** O que vai acontecer, em uma ou duas frases. Diga o efeito, não a mecânica. */
  corpo: ReactNode;
  /** O verbo do resultado. Vira o rótulo do botão. */
  verbo: string;
  /**
   * Só em `critica`: o texto que o operador precisa digitar para liberar o
   * botão. Costuma ser a referência do boleto ou a quantidade afetada.
   */
  digitarParaLiberar?: string;
  /**
   * Só em `critica`: exige justificativa, que vai para a auditoria.
   *
   * O mínimo de 10 caracteres não é capricho — "ok", "." e "n/a" são a forma
   * mais comum de derrotar uma exigência dessas, e a API recusa os três.
   */
  exigeJustificativa?: boolean;
  /**
   * Distingue dois pedidos com o mesmo título e verbo. Muda quando o pedido é
   * OUTRO apesar do texto igual — a confirmação de plano que o servidor devolve
   * com o mesmo número de contratos e outro conjunto —, e remonta o diálogo.
   */
  identidade?: string;
}

export function Confirmacao({
  pedido,
  aoConfirmar,
  aoCancelar,
}: {
  /** `null` fecha o diálogo. */
  pedido: PedidoDeConfirmacao | null;
  /** Recebe a justificativa quando ela foi exigida. Pode lançar: o erro aparece aqui. */
  aoConfirmar: (justificativa: string) => Promise<void>;
  aoCancelar: () => void;
}) {
  if (pedido === null) return null;

  /*
   * A `key` remonta o diálogo a cada pedido diferente, e é isso que garante
   * campos limpos — em vez de um `useEffect` que zera o estado na abertura.
   *
   * Não é preferência de estilo: zerar por efeito produz renderização em
   * cascata (o React reclama, com razão), e deixa uma janela em que o diálogo
   * já está na tela com o texto do pedido ANTERIOR. Numa justificativa que vai
   * para a auditoria, esse texto descreveria o fato errado.
   */
  return (
    <DialogoDeConfirmacao
      key={`${pedido.titulo}-${pedido.verbo}-${pedido.identidade ?? ""}`}
      pedido={pedido}
      aoConfirmar={aoConfirmar}
      aoCancelar={aoCancelar}
    />
  );
}

function DialogoDeConfirmacao({
  pedido,
  aoConfirmar,
  aoCancelar,
}: {
  pedido: PedidoDeConfirmacao;
  aoConfirmar: (justificativa: string) => Promise<void>;
  aoCancelar: () => void;
}) {
  const devolverFoco = useDevolverFoco(true);
  const idJustificativa = useId();
  const idDigitado = useId();
  const campoDoDigitado = useRef<HTMLInputElement>(null);
  const campoDaJustificativa = useRef<HTMLTextAreaElement>(null);

  const [digitado, setDigitado] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const precisaDigitar = pedido.digitarParaLiberar !== undefined;
  const precisaJustificar = pedido.exigeJustificativa === true;

  const digitouCerto = !precisaDigitar || digitado.trim() === pedido.digitarParaLiberar;
  const justificou = !precisaJustificar || justificativa.trim().length >= 10;
  const liberado = digitouCerto && justificou && !enviando;

  /*
   * Sem isto, o foco inicial cai no primeiro elemento focável, que é o botão da `Dica`:
   * ela abre sozinha e cobre a frase que diz a consequência da ação. É aqui, e não
   * `autoFocus` no campo: com o painel lateral aberto, a armadilha de foco dele puxaria o
   * foco de volta antes de este diálogo assumir.
   *
   * O primeiro campo na ordem de leitura: com o nome a digitar acima da justificativa, abrir
   * nela obrigava quem usa teclado a voltar um campo.
   */
  function focarPrimeiroCampo(evento: Event) {
    const campo = campoDoDigitado.current ?? campoDaJustificativa.current;
    if (campo === null) return;
    evento.preventDefault();
    campo.focus();
  }

  async function confirmar() {
    setErro(null);
    setEnviando(true);
    try {
      await aoConfirmar(justificativa.trim());
    } catch (falha) {
      // O erro aparece DENTRO do diálogo, e ele não fecha. Fechar num erro
      // faria o operador perder a justificativa que acabou de escrever, e não
      // saber se a ação aconteceu.
      setErro(textoDoErro(falha));
      setEnviando(false);
    }
  }

  return (
    <Dialogo.Root open onOpenChange={(estado) => !estado && !enviando && aoCancelar()}>
      <Dialogo.Portal>
        <Dialogo.Overlay className="sobreposicao fixed inset-0 z-[60] bg-black/60" />

        <Dialogo.Content
          onOpenAutoFocus={focarPrimeiroCampo}
          onCloseAutoFocus={devolverFoco}
          className="fixed left-1/2 top-1/2 z-[70] w-[440px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 rounded-cartao border border-fio bg-superficie p-5"
        >
          <Dialogo.Title className="flex items-start gap-2 text-titulo font-semibold text-texto">
            {pedido.severidade !== "leve" && (
              <AlertTriangle
                aria-hidden="true"
                className={`mt-[3px] size-4 shrink-0 ${
                  pedido.severidade === "critica" ? "text-indeterminado" : "text-falha"
                }`}
              />
            )}
            {pedido.titulo}
          </Dialogo.Title>

          <Dialogo.Description asChild>
            <div className="mt-2 text-texto-2">{pedido.corpo}</div>
          </Dialogo.Description>

          {precisaDigitar && (
            <div className="mt-4 flex flex-col gap-1">
              <label htmlFor={idDigitado} className="rotulo">
                Digite <span className="dados text-texto">{pedido.digitarParaLiberar}</span> para
                liberar
              </label>
              <input
                id={idDigitado}
                ref={campoDoDigitado}
                value={digitado}
                onChange={(evento) => setDigitado(evento.target.value)}
                autoComplete="off"
                className="dados h-9 rounded-controle border border-fio bg-superficie-2 px-3 text-corpo text-texto"
              />
            </div>
          )}

          {precisaJustificar && (
            <div className="mt-3 flex flex-col gap-1">
              {/*
                O mínimo de 10 caracteres vai na dica, e não em parágrafo: o botão
                travado já avisa que falta algo, e a dica diz o quê — e que o
                texto é a única evidência quando alguém perguntar meses depois.
              */}
              <div className="flex items-center gap-1">
                <label htmlFor={idJustificativa} className="rotulo">
                  Justificativa
                </label>
                <Dica texto="Vai para a auditoria. Mínimo de 10 caracteres." />
              </div>
              <textarea
                id={idJustificativa}
                ref={campoDaJustificativa}
                rows={3}
                value={justificativa}
                onChange={(evento) => setJustificativa(evento.target.value)}
                placeholder="O que você conferiu, e onde"
                className="rounded-controle border border-fio bg-superficie-2 px-3 py-2 text-corpo text-texto placeholder:text-texto-3"
              />
            </div>
          )}

          {erro !== null && (
            <p
              role="alert"
              className="mt-3 flex items-start gap-2 rounded-controle border border-falha/40 bg-falha/10 px-3 py-2 text-[12px] text-falha"
            >
              <AlertCircle aria-hidden="true" className="mt-[1px] size-3.5 shrink-0" />
              {erro}
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <Botao tom="secundario" onClick={aoCancelar} disabled={enviando}>
              Voltar
            </Botao>
            <Botao
              tom={pedido.severidade === "leve" ? "primario" : "destrutivo"}
              onClick={() => void confirmar()}
              disabled={!liberado}
              carregando={enviando}
            >
              {pedido.verbo}
            </Botao>
          </div>
        </Dialogo.Content>
      </Dialogo.Portal>
    </Dialogo.Root>
  );
}
